<template>
	<div class="mention-editor-container" :class="{ 'mention-editor-container--dark': isDark }">
		<!-- Rich Text Toolbar -->
		<div class="toolbar">
			<button type="button" @click="execCommand('bold')" :class="{ active: isActive('bold') }">
				<strong>B</strong>
			</button>
			<button type="button" @click="execCommand('italic')" :class="{ active: isActive('italic') }">
				<em>I</em>
			</button>
			<button type="button" @click="execCommand('underline')" :class="{ active: isActive('underline') }">
				<u>U</u>
			</button>
			<button type="button" @click="execCommand('insertUnorderedList')">
				• List
			</button>
			<button type="button" @click="execCommand('insertOrderedList')">
				1. List
			</button>
		</div>

		<!-- Content Editable Area -->
		<div
			:ref="refName"
			class="editor-content"
			contenteditable="true"
			@input="handleInput"
			@focus="handleFocus"
			@blur="handleBlur"
			@paste="handlePaste"
		></div>
	</div>
</template>

<script>
import Tribute from 'tributejs';
import { mapState } from 'vuex';

export default {
	name: 'MentionEditor',
	computed: {
		...mapState({
			isDark: state => state.currentUser.isDark,
		}),
	},
	props: {
		value: {
			type: String,
			required: false,
			default: '',
		},
		refName: {
			type: String,
			required: true,
		},
		users: {
			type: Array,
			required: false,
			default: () => [],
		},
		disabled: {
			type: Boolean,
			required: false,
			default: false,
		},
	},
	data() {
		return {
			tribute: null,
			changed: 0,
			isFocused: false,
		};
	},
	methods: {
		handleInput() {
			const now = new Date();
			this.changed = now.getTime();
			
			const content = this.$refs[this.refName].innerHTML;
			this.$emit('input', content);
		},
		
		saveSelection() {
			const selection = window.getSelection();
			if (selection.rangeCount > 0) {
				return selection.getRangeAt(0);
			}
			return null;
		},
		
		restoreSelection(range) {
			if (range) {
				const selection = window.getSelection();
				selection.removeAllRanges();
				selection.addRange(range);
			}
		},
		
		setContent(content) {
			const editor = this.$refs[this.refName];
			if (!editor) return;
			
			const wasEmpty = !editor.innerHTML || editor.innerHTML === '<div><br></div>';
			const willBeEmpty = !content || content === '<div><br></div>';
			
			const savedRange = this.saveSelection();
			editor.innerHTML = content || '<div><br></div>';
			
			// Reinitialize Tribute when content is cleared (after form submission)
			if (!wasEmpty && willBeEmpty && this.tribute) {
				this.$nextTick(() => {
					this.detachTribute();
					setTimeout(() => {
						this.initTribute();
					}, 100);
				});
			}
			
			// Restore cursor position
			this.$nextTick(() => {
				if (savedRange && !willBeEmpty) {
					try {
						this.restoreSelection(savedRange);
					} catch (e) {
						this.placeCursorAtEnd();
					}
				} else {
					this.placeCursorAtEnd();
				}
			});
		},
		
		placeCursorAtEnd() {
			const editor = this.$refs[this.refName];
			if (!editor) return;
			
			const range = document.createRange();
			const selection = window.getSelection();
			
			// Place cursor at the end of content
			range.selectNodeContents(editor);
			range.collapse(false);
			selection.removeAllRanges();
			selection.addRange(range);
		},
		
		handleFocus() {
			this.isFocused = true;
		},
		
		handleBlur() {
			this.isFocused = false;
		},
		
		execCommand(command, value = null) {
			document.execCommand(command, false, value);
			this.$refs[this.refName].focus();
			this.handleInput();
		},
		
		isActive(command) {
			return document.queryCommandState(command);
		},
		
		handlePaste(event) {
			event.preventDefault();
			const clipboardData = event.clipboardData || window.clipboardData;
			const pastedData = clipboardData.getData('text/html') || clipboardData.getData('text/plain');
			
			if (pastedData) {
				document.execCommand('insertHTML', false, pastedData);
				this.handleInput();
			}
		},
		
		initTribute() {
			// Always detach existing tribute first to prevent conflicts
			this.detachTribute();
			
			if (!this.$refs[this.refName] || !this.users || this.users.length === 0) {
				return;
			}
			
			const activeUsers = this.users.filter(user => user.active);
			
			if (activeUsers.length === 0) {
				return;
			}
			
			this.tribute = new Tribute({
				collection: [
					{
						trigger: '@',
						values: activeUsers.map(user => ({
							key: user.name || user.email,
							value: user.id,
						})),
						selectTemplate: (item) => {
							return `<span contenteditable="false" class="user-mention" data-user-id="${item.original.value}">@${item.original.key}</span>&nbsp;`;
						},
						menuItemTemplate: (item) => {
							return item.original.key;
						},
						noMatchTemplate: () => {
							return '<div style="padding: 8px 12px; color: #999;">No users found</div>';
						},
					},
				],
				allowSpaces: true,
				replaceTextSuffix: '',

			});
			
			this.tribute.attach(this.$refs[this.refName]);
		},
		
		detachTribute() {
			if (this.tribute && this.$refs[this.refName]) {
				try {
					this.tribute.detach(this.$refs[this.refName]);
				} catch (error) {
					// Silently handle detach errors - element might already be detached
				}
				this.tribute = null;
			}
			// Clean up any orphaned tribute containers
			this.hideTributeMenu();
		},
		
		hideTributeMenu() {
			// Clean up any orphaned tribute containers
			const tributeContainers = document.querySelectorAll('.tribute-container');
			tributeContainers.forEach(container => {
				container.remove();
			});
		},
		
		updateTribute() {
			this.detachTribute();
			this.$nextTick(() => {
				this.initTribute();
			});
		},
	},
	
	mounted() {
		// Set default paragraph separator for better formatting
		document.execCommand('defaultParagraphSeparator', false, 'div');
		
		// Set initial content
		if (this.value) {
			this.setContent(this.value);
		} else {
			// Ensure editor has a proper initial state
			this.$refs[this.refName].innerHTML = '<div><br></div>';
		}
		
		// Initialize Tribute.js after DOM is ready
		this.$nextTick(() => {
			setTimeout(() => {
				this.initTribute();
			}, 100);
		});
	},
	
	beforeDestroy() {
		this.detachTribute();
	},
	
	watch: {
		value: {
			handler(newVal) {
				const now = new Date();
				const { changed } = this;
				// Only update if change wasn't from user input and content actually changed
				if (now.getTime() - changed > 100) {
					const currentContent = this.$refs[this.refName] ? this.$refs[this.refName].innerHTML : '';
					// Only update if the content is actually different
					if ((newVal || '') !== currentContent) {
						this.setContent(newVal);
					}
				}
			},
			immediate: true,
		},
		
		users: {
			handler() {
				// Initialize Tribute only if it doesn't exist yet
				if (!this.tribute) {
					this.updateTribute();
				}
			},
			deep: true,
		},
		
		disabled: {
			handler(newVal) {
				if (this.$refs[this.refName]) {
					this.$refs[this.refName].contentEditable = !newVal;
				}
			},
			immediate: true,
		},
	},
};
</script>

<style lang="scss" scoped>
.mention-editor-container {
	border: 1px solid #cccccc;
	border-radius: 4px;
	background: white;
	
	.toolbar {
		display: flex;
		gap: 4px;
		padding: 8px;
		border-bottom: 1px solid #e0e0e0;
		background: #f8f9fa;
		
		button {
			padding: 6px 12px;
			border: 1px solid #ddd;
			background: white;
			border-radius: 3px;
			cursor: pointer;
			font-size: 14px;
			
			&:hover {
				background: #f0f0f0;
			}
			
			&.active {
				background: #007bff;
				color: white;
				border-color: #007bff;
			}
		}
	}
	
	.editor-content {
		min-height: 120px;
		padding: 12px;
		outline: none;
		line-height: 1.5;
		
		&:focus {
			box-shadow: inset 0 0 0 2px #007bff22;
		}
		
		&[contenteditable="false"] {
			background: #f8f9fa;
			color: #6c757d;
		}
	}
}
</style>

<style lang="scss">
// Global styles for mention content
.user-mention {
	font-weight: bold;
	background-color: #e3f2fd;
	padding: 2px 6px;
	border-radius: 3px;
	color: #1976d2;
	cursor: pointer;
	display: inline-block;
	margin: 0 1px;
	
	&:hover {
		background-color: #bbdefb;
	}
}

// Tribute.js menu styling - minimal overrides
.tribute-container {
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
	border-radius: 4px;
	z-index: 1000;
	
	ul {
		background: white;
		margin: 0;
		padding: 0;
		list-style: none;
		max-height: 200px;
		overflow-y: auto;
		border: 1px solid #ddd;
		border-radius: 4px;
		
		li {
			padding: 12px 16px;
			cursor: pointer;
			border-bottom: 1px solid #f0f0f0;
			
			&:last-child {
				border-bottom: none;
			}
			
			&.highlight {
				background: #e3f2fd;
			}
			
			&:hover {
				background: #e3f2fd;
			}
		}
	}
}

.mention-editor-container {
	&.mention-editor-container--dark {
		border: 1px solid #424242;
		background: #1e1e1e;
		
		.toolbar {
			border-bottom: 1px solid #424242;
			background: #2d2d2d;
			
			button {
				border: 1px solid #555;
				background: #3d3d3d;
				color: #ffffff;
				
				&:hover {
					background: #4d4d4d;
				}
				
				&.active {
					background: #0066cc;
					color: white;
					border-color: #0066cc;
				}
			}
		}
		
		.editor-content {
			background: #1e1e1e;
			color: #ffffff;
			
			&:focus {
				box-shadow: inset 0 0 0 2px #0066cc22;
			}
			
			&[contenteditable="false"] {
				background: #2d2d2d;
				color: #888888;
			}
		}
	}
}
</style>